import { Component, HostListener, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { DocumentService } from 'src/app/services/document.service';
import { FrontendLoggerService } from 'src/app/services/frontend-logger.service';
import { LanguageService } from 'src/app/services/language/language.service';
import { DocumentContextMenuCoordinator } from './document-context-menu.coordinator';
import { DocumentDownloadCoordinator } from './document-download.coordinator';
import { DocumentGroup, GroupedDocument } from './document-details.models';
import { DocumentListCoordinator } from './document-list.coordinator';

@Component({
  selector: 'app-document-details',
  templateUrl: './document-details.component.html',
  styleUrls: ['./document-details.component.context-menu-layout.scss', './document-details.component.context-menu-actions.scss'],
  standalone: false
})
export class DocumentDetailsComponent implements OnChanges {
  @Input() filterType: string | null = null;
  @Input() filterDomain: string | null = null;
  @Input() documentIds: string[] | null = null;

  downloadMessage = '';
  downloadMessageVisible = false;
  activeDownloadDocId: string | null = null;
  isLoading = true;
  errorMessage = '';
  groupedDocuments: DocumentGroup[] = [];
  groupedDocumentsRaw: DocumentGroup[] = [];
  contextMenuVisible = false;
  contextMenuX = 0;
  contextMenuY = 0;
  contextMenuDoc: GroupedDocument | null = null;
  confirmContext = false;

  private readonly listCoordinator: DocumentListCoordinator;
  private readonly downloadCoordinator: DocumentDownloadCoordinator;
  private readonly contextCoordinator: DocumentContextMenuCoordinator;

  constructor(
    documentService: DocumentService,
    public readonly languageService: LanguageService,
    router: Router,
    frontendLogger: FrontendLoggerService
  ) {
    this.listCoordinator = new DocumentListCoordinator(documentService);
    this.downloadCoordinator = new DocumentDownloadCoordinator(documentService, frontendLogger);
    this.contextCoordinator = new DocumentContextMenuCoordinator(router, frontendLogger);
  }

  closeContextMenu = (): void => {
    this.contextMenuVisible = false;
    this.contextMenuDoc = null;
    this.confirmContext = false;
    document.removeEventListener('click', this.closeContextMenu);
  };

  @HostListener('document:contextmenu', ['$event'])
  onGlobalContextMenu(event: MouseEvent): void {
    const placement = this.contextCoordinator.locate(event, this.groupedDocuments, this.confirmContext);
    if (!placement) {
      return;
    }
    this.contextMenuDoc = placement.document;
    this.contextMenuX = placement.x;
    this.contextMenuY = placement.y;
    this.contextMenuVisible = true;
    this.confirmContext = false;
    setTimeout(() => document.addEventListener('click', this.closeContextMenu), 0);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.contextMenuVisible) {
      this.closeContextMenu();
    }
  }

  beginContextQuestion(): void { this.confirmContext = true; }

  confirmContextSelection(confirmed: boolean): void {
    if (this.contextMenuDoc) {
      this.contextCoordinator.confirm(this.contextMenuDoc, confirmed);
    }
    this.closeContextMenu();
  }

  deleteRow(document: GroupedDocument): void {
    if (!document?.id) {
      return;
    }
    this.isLoading = true;
    this.listCoordinator.delete(document.id).subscribe({
      next: () => {
        this.loadDocuments();
        this.showDownloadMessage('Borrado');
      },
      error: () => {
        this.errorMessage = this.languageService.translate('documents.error_deleting');
        this.isLoading = false;
      }
    });
  }

  ngOnChanges(_changes: SimpleChanges): void {
    const hasFilters = !!(this.filterType || this.filterDomain || this.documentIds?.length);
    if (this.groupedDocumentsRaw.length > 0 && hasFilters) {
      this.applyFilters();
    } else {
      this.loadDocuments();
    }
  }

  loadDocuments(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.listCoordinator.load().subscribe({
      next: (groups) => {
        this.groupedDocumentsRaw = groups;
        this.applyFilters();
        this.isLoading = false;
      },
      error: () => {
        this.errorMessage = this.languageService.translate('documents.error_loading');
        this.isLoading = false;
      }
    });
  }

  applyFilters(): void {
    this.groupedDocuments = this.listCoordinator.filter(this.groupedDocumentsRaw, {
      type: this.filterType,
      domain: this.filterDomain,
      documentIds: this.documentIds
    });
  }

  translate(key: string, params?: Record<string, unknown>): string {
    let translation = this.languageService.translate(key);
    Object.entries(params || {}).forEach(([name, value]) => {
      translation = translation.replace(`{{${name}}}`, String(value));
    });
    return translation;
  }

  getFileIcon(filename: string): string { return this.downloadCoordinator.fileIcon(filename); }
  getIconClass(filename: string): string { return this.downloadCoordinator.iconClass(filename); }

  downloadDocument(document: GroupedDocument): void {
    this.downloadCoordinator.download(document, (url, id) => this.openDocumentUrl(url, id));
  }

  openDocumentUrl(publicUrl: string, documentId?: string): void {
    this.downloadCoordinator.open(publicUrl, documentId, {
      activated: (id) => { this.activeDownloadDocId = id; },
      opened: () => this.showDownloadMessage('Descargado')
    });
  }

  showDownloadMessage(message: string): void {
    this.downloadMessage = message;
    this.downloadMessageVisible = true;
    setTimeout(() => {
      this.downloadMessageVisible = false;
      this.activeDownloadDocId = null;
    }, 3000);
  }
}
